import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { SITE_URL, canonicalUrl } from '../seo/siteRoutes.js';

const PAGE_PATH = '/print-quality';
const PAGE_URL = canonicalUrl(PAGE_PATH);
const META_DESCRIPTION =
  'מדריך לבחירת שיטת מיתוג ב-Printeam: מתי DTF מתאים לביגוד ומתי סובלימציה מתאימה לפוליאסטר ולמוצרים ייעודיים.';

export default function PrintQuality() {
  return (
    <div className="min-h-screen py-12">
      <Helmet>
        {/* Structured data: Organization, Article, BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Printeam",
            url: canonicalUrl('/'),
            logo: `${SITE_URL}/logo_printee-optimized.webp`
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "איכות הדפסה – Printeam",
            description: META_DESCRIPTION,
            author: { "@type": "Organization", name: "Printeam" },
            publisher: {
              "@type": "Organization",
              name: "Printeam",
              logo: { "@type": "ImageObject", url: `${SITE_URL}/logo_printee-optimized.webp` }
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": PAGE_URL }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "ראשי", item: canonicalUrl('/') },
              { "@type": "ListItem", position: 2, name: "איכות הדפסה", item: PAGE_URL }
            ]
          })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-6">איכות הדפסה</h1>

        <section>
          <h2 className="text-2xl font-semibold mt-4 mb-2">בחירת שיטת המיתוג המתאימה</h2>
          <p className="text-gray-700">
            התוצאה תלויה בסוג המוצר, בהרכב הבד, בצבע הרקע ובשימוש המתוכנן. באתר ניתן
            להזמין מוצרים המיועדים למיתוג ב־DTF, ולפרויקטים מתאימים אפשר לבחון גם
            סובלימציה. הבחירה נעשית לפי המוצר בפועל ולא לפי שיטה אחת שמתאימה לכל חומר.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mt-6 mb-2">DTF לביגוד וטקסטיל</h2>
          <p className="text-gray-700">
            בתהליך DTF העיצוב מודפס על גבי סרט ייעודי ומועבר לבד בחום ובלחץ. השיטה
            מאפשרת לעבוד עם פריטים רבים מכותנה, פוליאסטר ותערובות, בכפוף להתאמה של
            הפריט ושל אזור המיתוג.
          </p>
          <h3 className="text-xl font-medium mt-4">מתי כדאי לשקול DTF?</h3>
          <ul className="list-disc ml-6 mt-3 text-gray-700">
            <li>כאשר העיצוב כולל כמה צבעים או פרטים קטנים.</li>
            <li>כאשר מדפיסים על בגד בהיר או כהה שתואם לתהליך.</li>
            <li>כאשר רוצים לבחור אזורי מיתוג שונים בפריט.</li>
          </ul>
          <p className="text-gray-700 mt-3">
            מראה ההדפס ועמידותו מושפעים מהבד, מהעיצוב, מהיישום ומהוראות הטיפול של
            המוצר. לכן חשוב לעיין בפרטי הפריט ולפעול לפי הוראות הכביסה המוצגות בו.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mt-6 mb-2">סובלימציה לפוליאסטר ולמוצרים מצופים</h2>
          <p className="text-gray-700">
            בסובלימציה הצבע מועבר בחום אל חומר מתאים. השיטה מיועדת בעיקר לבדי
            פוליאסטר בהירים ולמוצרים בעלי ציפוי שמותאם לסובלימציה; היא אינה חלופה
            אוטומטית לכל בגד או מוצר.
          </p>
          <h3 className="text-xl font-medium mt-4">מאפייני השיטה</h3>
          <ul className="list-disc ml-6 mt-3 text-gray-700">
            <li>הצבע נטמע בחומר המתאים ואינו יוצר שכבה מורגשת מעל פני הבד.</li>
            <li>התוצאה מושפעת מצבע הבסיס ומהרכב החומר.</li>
            <li>יש לוודא שהמוצר מתאים לשיטה לפני אישור ההפקה.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mt-6 mb-2">מה כדאי לבדוק לפני ההזמנה?</h2>
          <ul className="list-disc ml-6 mt-3 text-gray-700">
            <li>שהקובץ חד וברזולוציה שמתאימה לגודל ההדפסה.</li>
            <li>שצבע הפריט והרכב הבד מתאימים לשיטת המיתוג.</li>
            <li>שאזורי המיתוג שנבחרו מתאימים לעיצוב ולגזרת הפריט.</li>
            <li>שהכמות, המידות והצבעים נכונים לפני המעבר לתשלום.</li>
          </ul>
        </section>

        <section className="mt-8">
          <h2 className="text-2xl font-semibold mb-2">מידע נוסף לפני שמתחילים</h2>
          <p className="text-gray-700">
            בעמוד <Link to="/faq" className="text-blue-600 hover:underline">השאלות הנפוצות</Link> מופיע
            מידע על פורמטים מומלצים לקבצים, כמויות ומשלוחים. לבחירת מוצר ולהגדרת
            אזורי המיתוג עברו אל <Link to="/catalog" className="text-blue-600 hover:underline">הקטלוג</Link>.
          </p>
        </section>
      </div>
    </div>
  );
}
